import { useCallback, useEffect } from "react";
import { imageGalleryCursorAtom, imageGalleryHasZoomAtom, imageGalleryImageZoomTransformAtom } from "./imageZoomState";
import { useRecoilState, useSetRecoilState } from "recoil";

const useHandleImageZoom = ({
  imageContainerRef,
  openModal,
}: {
  imageContainerRef: React.RefObject<HTMLImageElement>;
  openModal: boolean;
}) => {
  const setCursor = useSetRecoilState(imageGalleryCursorAtom);
  const setImageTransform = useSetRecoilState(imageGalleryImageZoomTransformAtom);
  const [hasZoom, setHasZoom] = useRecoilState(imageGalleryHasZoomAtom);

  const handleClickOutsideImage = useCallback(
    (e: MouseEvent) => {
      if (hasZoom && imageContainerRef.current) {
        const imageContainer = imageContainerRef.current;
        const target = e.target as Node;

        if (!imageContainer.contains(target)) {
          setCursor("default");
          setImageTransform({ x: 0, y: 0, scale: 1 });
          setHasZoom(false);
        }
      }
    },
    [hasZoom, imageContainerRef, setCursor, setHasZoom, setImageTransform]
  );

  useEffect(() => {
    if (hasZoom && openModal) {
      document.addEventListener("mousedown", handleClickOutsideImage);
      return () => {
        document.removeEventListener("mousedown", handleClickOutsideImage);
      };
    }
  }, [hasZoom, openModal, handleClickOutsideImage]);

  const handleNavigateZoomedImage = useCallback(
    (e: React.MouseEvent) => {
      if (hasZoom && imageContainerRef.current) {
        const container = imageContainerRef.current;
        const rect = container.getBoundingClientRect();

        const mouseXPercent = (e.clientX - rect.left) / rect.width;
        const mouseYPercent = (e.clientY - rect.top) / rect.height;

        const clampedMouseXPercent = Math.max(0, Math.min(1, mouseXPercent));
        const clampedMouseYPercent = Math.max(0, Math.min(1, mouseYPercent));

        const containerWidth = rect.width;
        const containerHeight = rect.height;

        const maxMoveX = containerWidth * 0.5;
        const maxMoveY = containerHeight * 0.7;

        const translateX = maxMoveX - clampedMouseXPercent * 2 * maxMoveX;
        const translateY = maxMoveY - clampedMouseYPercent * 2 * maxMoveY;

        setImageTransform((prev) => ({
          ...prev,
          x: translateX,
          y: translateY,
        }));
      }
    },
    [hasZoom, imageContainerRef, setImageTransform]
  );

  const handleZoom = useCallback(() => {
    if (hasZoom) {
      setHasZoom(false);
      setCursor("zoom-in");
      setImageTransform({ x: 0, y: 0, scale: 1 });
    } else {
      setHasZoom(true);
      setCursor("zoom-out");
      setImageTransform({ x: 0, y: 0, scale: 2 });
    }
  }, [hasZoom, setCursor, setHasZoom, setImageTransform]);

  const handleMouseEnterImage = useCallback(() => {
    if (hasZoom) return;
    setCursor("zoom-in");
  }, [hasZoom, setCursor]);

  return { handleZoom, handleNavigateZoomedImage, handleMouseEnterImage };
};

export default useHandleImageZoom;
